// Standalone chrome (Nav + Footer) extracted from _v03_earthy.jsx so it
// can be mounted on interior portal pages, not just the homepage.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#ece6d8', fg: '#2b2820', muted: '#6e6a5c',
  border: '#d6cfbb', accent: '#5b6b46', accentFg: '#fbfaf3',
  inputBg: '#fbfaf3', radius: 14,
  font: '"DM Sans", system-ui, sans-serif',
  offWhite: '#fbfaf3',
  sageAlt: '#e4ecdf',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navLinks = [
  { label: 'Wärmepumpe', href: '/wärmepumpe' },
  { label: 'Solar', href: '/photovoltaik' },
  { label: 'Förderung', href: '/förderung-finder' },
  { label: 'Über uns', href: '/über-uns' },
];

const footerLinks = [
  { label: 'Impressum', href: '/impressum' },
  { label: 'Datenschutz', href: '/datenschutz' },
  { label: 'AGB', href: '/agb' },
  { label: 'Widerruf', href: '/widerruf' },
  { label: 'Kontakt', href: '/kontakt' },
];

export function V03EarthyNav({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <div style={{
      padding: isMobile ? '14px 16px' : '20px 48px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      background: theme.bg, color: theme.fg, fontFamily: theme.font,
      position: 'relative',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 26, height: 26, borderRadius: 13, background: theme.accent }}/>
        <div style={{ fontWeight: 600, fontSize: 17, letterSpacing: '-0.01em' }}>{brand}</div>
      </div>
      {!isMobile && (
        <>
          <div style={{ display: 'flex', gap: 28, fontSize: 14, color: theme.muted }}>
            {navLinks.map(l => <Link key={l.label} href={l.href} style={{ color: theme.muted, textDecoration: 'none' }}>{l.label}</Link>)}
          </div>
          <Link href="/angebot-vergleichen" style={{
            padding: '10px 18px', fontSize: 13, fontWeight: 500, fontFamily: theme.font,
            background: 'transparent', color: theme.fg, border: `1.5px solid ${theme.fg}`,
            borderRadius: 999, cursor: 'pointer', textDecoration: 'none', display: 'inline-block',
          }}>Beratung anfordern</Link>
        </>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.bg, borderBottom: `1px solid ${theme.border}`,
          padding: '16px', display: 'flex', flexDirection: 'column', gap: 14,
          zIndex: 50,
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 15 }}>{l.label}</Link>
          ))}
          <Link href="/angebot-vergleichen" style={{
            padding: '10px 18px', fontSize: 14, fontWeight: 500,
            background: 'transparent', color: theme.fg, border: `1.5px solid ${theme.fg}`,
            borderRadius: 999, textAlign: 'center',
            textDecoration: 'none', display: 'block', marginTop: 4,
          }}>Beratung anfordern</Link>
        </div>
      )}
    </div>
  );
}

export function V03EarthyFooter({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  return (
    <footer style={{
      padding: isMobile ? '24px 16px' : '40px 48px',
      background: theme.offWhite,
      borderTop: `1px solid ${theme.border}`,
      color: theme.fg,
      fontFamily: theme.font,
    }}>
      <div style={{
        maxWidth: 1184, margin: '0 auto',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 16 : 32, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 22, height: 22, borderRadius: 11, background: theme.accent }}/>
          <div style={{ fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em', color: theme.fg }}>
            {brand}
          </div>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 10 : 28,
          fontSize: isMobile ? 13 : 14, color: theme.muted,
        }}>
          {footerLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ cursor: 'pointer', color: theme.muted, textDecoration: 'none' }}>{l.label}</Link>
          ))}
        </div>
        <div style={{ fontSize: 12, color: theme.muted }}>{`© ${new Date().getFullYear()} · alle Rechte vorbehalten
        `}</div>
      </div>
    </footer>
  );
}
